import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Cloud, Globe, Brain, Building2, Rocket, Headset, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { mockServices, mockStats } from '../mock';
import SEO from '../components/SEO';
import CountUp from '../components/CountUp';
import DarkPattern from '../components/DarkPattern';
import { blogPosts } from './Blog';

const Home = () => {
  const iconMap = {
    Brain: Brain,
    Users: Users,
    Cloud: Cloud,
    Building2: Building2
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="AI-Enabled Teams for Cloud, DevOps & GCC | NIVA Digital AI"
        description="Build, Operate, Transfer with NIVA Digital. Access world-class AI-enabled teams for infrastructure, DevOps, CloudOps & GCC. Save 40-60% with 99.9% uptime and HIPAA-compliant delivery from India."
        keywords="infrastructure teams, PODs, GCC consulting India, cloud managed services, DevOps solutions, CloudOps RaaS, HIPAA compliant infrastructure, development center"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/`}
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 overflow-hidden">
        {/* Faded realistic tech background */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/home-hero-bg.jpg')" }}
          aria-hidden="true"
        />
        {/* Light overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white/70" aria-hidden="true" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Engineering Teams
              <span className="block text-red-600 mt-2">Built for Scale</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Dedicated AI-Enabled Engineering Teams
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/gcc-cost-calculator">
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
                  data-testid="gcc-cost-calculator-btn"
                >
                  GCC Cost Calculator
                </Button>
              </Link>
              <Link to="/services/offshore-pods">
                <Button size="lg" variant="outline" className="px-8 py-6 text-lg border-gray-300 hover:border-red-600 hover:text-red-600">
                  Learn More
                </Button>
              </Link>
            </div>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Not sure where to start? <Link to="/contact" className="text-red-600 hover:text-red-700 underline underline-offset-4" data-testid="contact-us-link">Contact us.</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockStats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-red-600 mb-2">
                  <CountUp value={stat.value} />
                </p>
                <p className="text-gray-600 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions to accelerate your digital transformation
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {mockServices.filter((service) => service.id !== 'ai-services').map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const displayTitle = service.id === 'bot-consulting' ? 'GCC Consulting' : service.title;
              return (
                <Card key={service.id} className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-xl">
                  <CardHeader>
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-red-600" size={28} />
                    </div>
                    <CardTitle className="text-2xl mb-2">{displayTitle}</CardTitle>
                    <CardDescription className="text-base">{service.shortDescription}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={service.route || `/services/${service.slug}`}>
                      <Button variant="outline" className="w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white">
                        Learn More <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose NIVA Digital?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Enterprise-grade expertise with cost efficiency
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Rocket className="text-red-600" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Speed & Expertise</h3>
              <p className="text-gray-600">
                Access experienced engineers proficient in modern tools, technologies, and frameworks
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="text-red-600" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Global Delivery Model</h3>
              <p className="text-gray-600">
                Round-the-clock productivity with dedicated teams operating as an extension of your in-house team
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Headset className="text-red-600" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">End-to-End Support</h3>
              <p className="text-gray-600">
                Comprehensive services covering every stage of your infrastructure journey
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real-world results from our client engagements
            </p>
          </div>

          <Card className="border-2 border-red-600 hover:shadow-2xl transition-all duration-300">
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Senior Living & Healthcare Transformation
                  </h3>
                  <p className="text-lg text-gray-600 mb-6">
                    HIPAA-compliant cloud infrastructure that centralized patient data, integrated EHR systems, and ensured seamless access for caregivers
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">99.98%</p>
                      <p className="text-sm text-gray-600">Uptime</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">40%</p>
                      <p className="text-sm text-gray-600">Cost Reduction</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">50%</p>
                      <p className="text-sm text-gray-600">Faster Releases</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">100%</p>
                      <p className="text-sm text-gray-600">Compliant</p>
                    </div>
                  </div>
                  <Link to="/case-studies">
                    <Button className="bg-red-600 hover:bg-red-700 text-white">
                      View Use Cases <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </div>
                <div className="bg-gradient-to-br from-blue-100 to-indigo-50 rounded-2xl p-8">
                  <Shield className="text-blue-600 mx-auto mb-4" size={80} />
                  <p className="text-center text-gray-700 italic">
                    &ldquo;NIVA Digital doesn&apos;t just provide IT services, they collaborate as true partners.&rdquo;
                  </p>
                  <p className="text-center text-gray-900 font-semibold mt-2">
                    - VP of Partnerships, Health-tech Startup
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Delivery Model Section */}
      <section className="relative overflow-hidden py-20 px-4 bg-gray-900 text-white" data-testid="delivery-model-section">
        <DarkPattern variant="flow" glow="tr" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-14">
            <p className="text-sm font-bold tracking-widest text-red-500 uppercase mb-3">How We Deliver</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Six phases from discovery<br className="hidden md:block" /> to <span className="text-red-500 italic">operated production</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Every engagement runs on our proven Build-Operate-Transfer framework, structured phases, quality-gated milestones, and operated delivery that never leaves you exposed.
            </p>
          </div>

          <div className="text-center mt-8">
            <Link to="/delivery">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg" data-testid="delivery-model-cta">
                See How Operated Delivery Works <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 px-4 bg-white" data-testid="home-blog-section">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Latest Insights
              </h2>
            </div>
            <Link to="/blog">
              <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white" data-testid="home-blog-viewall">
                View All Articles <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.slice(0, 3).map((post) => (
              <a
                key={post.id}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`home-blog-card-${post.id}`}
                className="group flex flex-col rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-red-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute top-4 left-4 text-xs font-bold px-3 py-1 rounded-full bg-red-600 text-white">
                    {post.category}
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug group-hover:text-red-600 transition-colors line-clamp-3">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5 line-clamp-3">{post.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between text-xs text-gray-500">
                    <span className="font-medium text-gray-700">{post.author}</span>
                    <span>{post.date} · {post.readTime}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <p className="text-lg text-gray-600 mb-6">
              Partner with NIVA Digital to collaborate, innovate and transform
            </p>
            <Link to="/partnerships">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-4">
                Explore Partnership Opportunities <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <Card className="border-2 border-red-600 bg-gradient-to-br from-red-50 to-orange-50 hover:shadow-2xl transition-all duration-300">
            <CardContent className="pt-12 pb-12">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="text-white" size={40} />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">Join Our Team</h3>
                    <p className="text-lg text-gray-700">
                      Be part of a dynamic team building the future
                    </p>
                  </div>
                </div>
                <a 
                  href="https://nivadigital.zohorecruit.in/jobs/Careers"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg whitespace-nowrap">
                    Explore Careers <ArrowRight className="ml-2" size={20} />
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Organization?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Let&apos;s discuss how NIVA Digital can empower your business with dedicated teams
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Schedule Free Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;